import React, { useState, useEffect } from "react";
import { Button, Popover, OverlayTrigger, Accordion } from "react-bootstrap";
import Select from "react-select";
import { ThreeDots } from "react-loader-spinner";
import Alert from "../common/Alert";
import WellSummaryService from "../../api-service/WellSummaryService";
import { ERROR_FETCHING_WELL_SUMMARY_DETAILS } from "../../configurations/MessageConstants";
import { DANGER, EMPTY, START_YEAR } from "../../configurations/Constants";
import { StatusCodes } from "../../configurations/status-codes";

let yearData = [];

function TabSummary(props) {
  const [wells, setWells] = useState([]);
  const [year, setYear] = useState({
    value: new Date().getFullYear(),
    label: new Date().getFullYear(),
  });
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [alertMessage, setAlertMessage] = useState(EMPTY);
  const [variant, setVariant] = useState(EMPTY);
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => {
    (async function () {
      yearData = getYearDataArray(yearData);
      const currentSeasonYear = getSeasonalYear();
      getYearWellSummary(currentSeasonYear);
    })();
  }, []);

  const getYearDataArray = (yearData) => {
    let runningYear = new Date().getFullYear();
    for (let i = runningYear; i >= START_YEAR; i--) {
      if (!yearData.some((objValue) => objValue.label === i)) {
        let yearobj = {
          value: i,
          label: i,
        };
        yearData.push(yearobj);
      }
    }
    return yearData;
  };

  const getSeasonalYear = () => {
    const currentSeasonYear = new Date().getFullYear();
    const dateForNewSeason = new Date(`01/APR/${currentSeasonYear}`).getTime();
    const dataToday = new Date().getTime();
    return dataToday < dateForNewSeason
      ? currentSeasonYear - 1
      : currentSeasonYear;
  };

  const getYearWellSummary = async (yearValue) => {
    try {
      setYear({
        value: yearValue,
        label: yearValue,
      });
      setLoadingSummary(true);

      const payload = {
        user_field_id: props.fieldDetails.field_id
          ? props.fieldDetails.field_id
          : props.fieldDetails.user_field_id,
        year: yearValue,
      };
      const response = await WellSummaryService.getWellSummary(payload);
      const wellData = response.data.data.well_data;
      setWells(wellData);
      setLoadingSummary(false);
    } catch (error) {
      console.error(ERROR_FETCHING_WELL_SUMMARY_DETAILS, error);
      if (error.response.status === StatusCodes.BAD_REQUEST) {
        setLoadingSummary(false);
        setAlertMessage(ERROR_FETCHING_WELL_SUMMARY_DETAILS);
        setVariant(DANGER);
        setShowAlert(true);
      }
    }
  };

  const handleCloseAlert = () => {
    setAlertMessage(EMPTY);
    setVariant(EMPTY);
    setShowAlert(false);
  };

  return (
    <div className="summary">
      <div className="d-flex justify-content-between">
        <h4 className="summary-label" xs={8}>
          <p className="mt-3 pe-2">
            <strong>Irrig Unit ID:</strong> {props.fieldDetails.irrig_unit_id}
            &nbsp;&nbsp;&nbsp;
            <strong>Irr Acres:</strong> {props.fieldDetails.calculated_acres}
          </p>
          <OverlayTrigger
            trigger="click"
            rootClose
            placement="bottom"
            overlay={
              <Popover id="info-popover">
                <Popover.Body>
                  <p>
                    * Well pump rate (rate of water volume per minute).
                    Referenced directly to the well: audit pump rate or user
                    pump rate or district pump rate.
                  </p>
                  <p>
                    * Run time: provided by the “meter” – most meters |
                    locations are electric and are derived from the run time
                    algo for the particular power company.
                  </p>
                  <p>* Pump rate X run time = water volume (gallons)</p>
                  <p>
                    * Acre feet = Water Volume (gallons) / 325,851.43 (gallons)
                  </p>
                  <p>* Acre inches = Acre feet X 12 (inches)</p>
                  <p>* Inches = Acre inches / acres</p>
                </Popover.Body>
              </Popover>
            }
          >
            <Button
              variant="secondary"
              className="info-button fa fa-info-circle"
            ></Button>
          </OverlayTrigger>
        </h4>
        <div className="summary-year">
          <Select
            name="year"
            options={yearData}
            className="basic-multi-select"
            onChange={(e) => getYearWellSummary(e.value)}
            value={year}
          />
        </div>
      </div>

      {loadingSummary ? (
        <div className="d-flex justify-content-center">
          <ThreeDots color="#00BFFF" height={80} width={80} />
        </div>
      ) : showAlert ? (
        <Alert
          key="messageAlert"
          variant={variant}
          closeAlert={(e) => handleCloseAlert(e)}
          alertMessage={alertMessage}
          dismissible
        />
      ) : (
        <Accordion defaultActiveKey="0">
          {wells.map((well, idx) => (
            <Accordion.Item key={well.id} eventKey={`${idx}`}>
              <Accordion.Header>
                <div>
                  <span
                    className="summary-accordion-header-well-id"
                    onClick={(e) => e.stopPropagation()}
                    onSelect={(e) => e.stopPropagation()}
                  >
                    Well {well.well_id}
                  </span>
                  <span className={"wellcolor" + idx}>&nbsp; &#9679; </span>
                </div>
              </Accordion.Header>
              <Accordion.Body className="summary-left-details">
                <div>
                  <p>
                    <b>Inches:</b> {well.metrics.inches_per_acre}
                  </p>
                  <p>
                    <b>Hours Run:</b> {well.metrics.well_runtime}
                  </p>
                  <p>
                    <b>Year Pump Rate:</b>{" "}
                    {well.audited_pump_rate
                      ? well.audited_pump_rate
                      : well.updated_pump_rate
                      ? well.updated_pump_rate
                      : well.pump_rate}
                  </p>
                </div>
                <div>
                  <p>
                    <b>Meter:</b> {well.well_meter}
                  </p>
                  <p>
                    <b>Source:</b> {well.power_co}
                  </p>
                </div>
                <div>
                  <p>
                    <b>Testing Company:</b> {well.testing_company}
                  </p>
                  <p>
                    <b>Most Recent Audit:</b> {well.audit_date}
                  </p>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      <h6 className="mt-4">
        NOTE: In case you feel that any of the details provided are incorrect,
        please get in touch with us by clicking on the REPORT option and we will
        try to get it resolved.
      </h6>
    </div>
  );
}

export default TabSummary;
